import React, { useEffect } from 'react';
import { signInWithGoogle } from './firebase';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import AnimatedLogo from './AnimatedLogo';
import { Link } from 'react-router-dom';
import NavBar from './NavBar';
import './Home.css';

function Home() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="home">
      <NavBar />

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <AnimatedLogo size={200} />
          <h1 className="hero-title">Orchestral AI</h1>
          <p className="hero-subtitle">Building agents to accelerate science</p>
          <p className="hero-description">
            Orchestrating LLM agents to solve complex tasks
          </p>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="philosophy">
        <div className="philosophy-container">
          <h2 className="section-title">Philosophy of Abstraction</h2>
          <div className="philosophy-content">
            <blockquote className="philosophy-quote">
              "Civilization advances by extending the number of important operations which we can perform without thinking of them."
              <cite>— Alfred North Whitehead</cite>
            </blockquote>
            <p className="philosophy-text">
              Both human and AI researchers benefit from building tools that abstract previously difficult operations.
              Agents can be created to handle complex operations and called by higher-level agents that orchestrate
              sophisticated research workflows. Such hierarchical systems show great promise in extending the capabilities
              and <em>coherence horizon</em> of the base LLMs composing them.
            </p>
            <p className="philosophy-text">
              Orchestral AI enables rapid prototyping of agentic systems for real-world research. From automating experiments
              and reproducing papers to managing Python environments and executing GitHub workflows, its modular architecture
              turns complex multi-step tasks into simple agent calls.
            </p>
          </div>
        </div>
      </section>

      {/* Code Examples Section */}
      <section className="examples">
        <div className="examples-container">
          <h2 className="section-title">Ease of Implementation</h2>

          <div className="examples-grid">
            {/* Simple Agent Example */}
            <div className="example-card">
              <h3 className="example-title"><Link to="/features#easy-agent-setup" className="title-link">Define an agent in a single line</Link></h3>
              <SyntaxHighlighter language="python" style={vscDarkPlus} customStyle={{ borderRadius: '8px', fontSize: '0.9rem' }}>
{`from orchestral import Agent

agent = Agent()

response = agent.run('Hello world!')
print(response.text)`}
              </SyntaxHighlighter>
              <div className="example-description">
                <p>The Orchestral platform abstracts away all the usual overhead:</p>
                <ul>
                  <li><Link to="/features#provider-agnostic" className="feature-link">Interacting with provider APIs</Link></li>
                  <li><Link to="/features#multi-turn-execution" className="feature-link">Multi-turn conversation flow</Link></li>
                  <li><Link to="/features#simple-tool-creation" className="feature-link">Handling tool calls</Link></li>
                  <li><Link to="/features#cost-tracking" className="feature-link">Tracking costs</Link></li>
                </ul>
              </div>
            </div>

            {/* Tool Definition Example */}
            <div className="example-card">
              <h3 className="example-title"><Link to="/features#simple-tool-creation" className="title-link">Define tools as simple functions</Link></h3>
              <SyntaxHighlighter language="python" style={vscDarkPlus} customStyle={{ borderRadius: '8px', fontSize: '0.9rem' }}>
{`from orchestral import Agent
from orchestral import define_tool

@define_tool()
def divide_tool(a: float, b: float = 1.0):
    """Divide two numbers a and b
    Returns a / b
    """
    return a / b

agent = Agent(tools=[divide_tool])`}
              </SyntaxHighlighter>
              <div className="example-description">
                <p>No need to wrestle with complex schemas or provider-specific tool formats. <Link to="/features#simple-tool-creation" className="feature-link">Orchestral automatically handles the entire zoo of different APIs and standards across providers.</Link></p>
              </div>
            </div>

            {/* Multi-Provider Example */}
            <div className="example-card">
              <h3 className="example-title"><Link to="/features#provider-agnostic" className="title-link">Switch between providers effortlessly</Link></h3>
              <SyntaxHighlighter language="python" style={vscDarkPlus} customStyle={{ borderRadius: '8px', fontSize: '0.9rem' }}>
{`from orchestral import Agent
from orchestral.llm import *

# llm = Claude()
# llm = Gemini()
# llm = Groq()
# llm = Llama()
# llm = LMStudio()
llm = GPT(model='gpt-5')

agent = Agent(llm=llm)`}
              </SyntaxHighlighter>
              <div className="example-description">
                <p>Support for all popular models with a unified interface. Each provider has different APIs, parameters, and quirks—<Link to="/features#provider-agnostic" className="feature-link">Orchestral handles them all seamlessly.</Link></p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Getting Started Section */}
      <section className="getting-started">
        <div className="getting-started-container">
          <h2 className="section-title">Get Started in Seconds</h2>
          <p className="getting-started-description">
            Install with pip, set your API keys, and start building intelligent agents right away
          </p>
          <div className="install-code">
            <SyntaxHighlighter language="bash" style={vscDarkPlus} customStyle={{ borderRadius: '8px', fontSize: '1.1rem', padding: '1.5rem' }}>
              pip install orchestral-ai
            </SyntaxHighlighter>
          </div>
          <p className="getting-started-note">
            No complex setup required. Just set your API keys and you're ready to build.
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section className="features">
        <div className="features-container">
          <h2 className="section-title">Why Orchestral?</h2>
          <p style={{ textAlign: 'center', marginTop: '1rem', marginBottom: '2rem' }}>
            <Link to="/features" style={{ color: '#d7af00', textDecoration: 'none', fontSize: '1.1rem' }}>
              See all features →
            </Link>
          </p>

          <div className="features-grid">
            <Link to="/features#simple-tool-creation" className="feature-card">
              <h3>🛠️ Simple Tool Creation</h3>
              <p>
                Define powerful tools with just a few lines of code. Auto-generated schemas
                and built-in parameter validation make tool creation effortless.
              </p>
            </Link>

            <Link to="/features#easy-agent-setup" className="feature-card">
              <h3>🤖 Easy Agent Setup</h3>
              <p>
                Create intelligent agents in seconds. Register tools, configure context,
                and start orchestrating - no boilerplate required.
              </p>
            </Link>

            <Link to="/features#streaming-ui" className="feature-card">
              <h3>⚡ Streaming UI</h3>
              <p>
                Real-time streaming responses with rich terminal UI. Watch your agents
                think and act with beautiful, responsive displays.
              </p>
            </Link>

            <Link to="/features#multi-turn-execution" className="feature-card">
              <h3>🔄 Multi-Turn Execution</h3>
              <p>
                Automatic tool execution loops let agents chain operations seamlessly.
                Complex workflows become simple agent calls.
              </p>
            </Link>

            <Link to="/features#provider-agnostic" className="feature-card">
              <h3>🧩 Provider Agnostic</h3>
              <p>
                Support for all popular LLM providers including local inference. Unified interface
                means switching providers is trivial.
              </p>
            </Link>

            <Link to="/features#smart-context" className="feature-card">
              <h3>💡 Smart Context</h3>
              <p>
                Intelligent message history management with caching. Keep conversations
                efficient without sacrificing context.
              </p>
            </Link>

            <Link to="/features#cost-tracking" className="feature-card">
              <h3>💰 Automatic Cost Tracking</h3>
              <p>
                Built-in token usage and cost tracking across all providers. Monitor spending
                in real-time without manual calculations.
              </p>
            </Link>

            <Link to="/features#lightweight-modular" className="feature-card">
              <h3>📦 Lightweight & Modular</h3>
              <p>
                Clean, minimal codebase with zero unnecessary dependencies. Use only what you need
                for maximum flexibility and performance.
              </p>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta">
        <div className="cta-content">
          <h2>Ready to Build?</h2>
          <p>Join the closed beta and start orchestrating AI agents today</p>
          <button className="btn btn-primary" onClick={signInWithGoogle}>
            Get Started
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <p><Link to="/about" style={{ color: 'inherit', textDecoration: 'none' }}>Created by Alex Roman and Jake Roman in 2023</Link></p>
      </footer>
    </div>
  );
}

export default Home;
